import { reactive, toRefs, nextTick, set, computed } from 'vue'
import { MessageBox } from 'element-ui'
import moment from 'moment'
import { clone } from '@/common/js/just.js'
import { Message } from 'view-design'
import { getUid, getDuration, getKeywordList, getNextRange, getRowEditStatus } from '../utils.js'
import { getMaterialScriptDetail, scriptOptimizeCreate, scriptShotMatch, scriptSave } from '@/api/material.model.js'
import { getAiStrategys } from '@/api/tools.model'

export const OPTIMIZE_FLAG = {
  GLOBAL: 1,
  SHOT: 2
}

/**
 * @description: 初始化脚本编辑表格、操作和编辑
 * @return {*}
 */
export const useScriptTableInit = () => {
  const model = reactive({
    scriptInfo: { scriptName: `未命名脚本_${moment().format('YYYYMMDD')}` },
    tableData: [],
    shotMaterialModalVisible: false, // 素材池弹窗
    shotMaterialList: [], // 素材池弹窗列表
    matchMaterialLoading: false,
    currentMaterialRowIndex: -1,
    currEditRow: {},
    matchThreshold: 30
  })

  /**
 * @description: 获取素材分析配置
 * @return {*}
 */
  async function getSettingList() {
    const [globalStrategys] = await getAiStrategys()
    const { promptContent: matchThreshold } = globalStrategys.strategies.find(s => s.strategyType === 3 && s.promptName === 'Matching_Threshold')
    if (matchThreshold) {
      model.matchThreshold = +matchThreshold
    }
    return Promise.resolve(model.matchThreshold)
  }

  /**
   * @description: 灵感创作或优化现有脚本过来的脚本参数编辑
   * @param {*} scriptContent
   * @return {*}
   */
  function makeTableByParams(scriptContent) {
    model.tableData = scriptContent.shots.map((s) => ({
      // 为每一行生成稳定唯一的 uid，避免编辑时 row-key 改变
      uid: getUid(),
      ...s,
      ...getRowEditStatus(),
      materialCheckedId: '', // 素材池勾选id
      compareShotChecked: '1', // 脚本智能优化对比默认选中
      matchThreshold: model.matchThreshold, // 片段匹配阈值
      key_elements: s.key_elements.join(',')
    }))
    getScriptShotMaterialItems(scriptContent)
  }

  /**
   * @description: 获取脚本详情
   * @param {*} scriptId
   * @return {*}
   */
  function getScriptDetail(scriptId) {
    return getMaterialScriptDetail({ scriptId }).then((data) => {
      if (data) {
        model.scriptInfo = data
        const scriptContent = JSON.parse(data.scriptContent)
        model.tableData = scriptContent.shots.map((s) => ({
          // 为每一行生成稳定唯一的 uid，避免编辑时 row-key 改变
          uid: getUid(),
          ...s,
          ...getRowEditStatus(),
          materialCheckedId: '', // 素材池勾选id
          compareShotChecked: '1', // 脚本智能优化对比默认选中
          matchThreshold: s.match_threshold || model.matchThreshold, // 片段匹配阈值
          key_elements: s.key_elements.join(',')
        }))

        getScriptShotMaterialItems(scriptContent)
      }
    })
  }

  /**
   * @description: 获取脚本全部分镜素材
   * @return {*}
   */
  function getScriptShotMaterialItems(scriptContent) {
    const hasMathchedMaterials = scriptContent.shots.some((s) => !!s.videos?.length)

    // 如果有匹配的视频，则不再进行api请求匹配
    if (hasMathchedMaterials) {
      model.tableData = model.tableData.map((s) => ({ ...s, materials: s.videos }))
      model.tableData.forEach((s) => {
        const checkedId = s.videos.find((v) => v.selected)?.id
        if (checkedId) {
          set(s, 'materialCheckedId', checkedId)
        }
      })
      return
    }
    matchByScript()
  }

  function matchByScript() {
    const segments = model.tableData.map((s) => {
      return {
        visual_core: s.visual_core,
        content_narrative_focus: s.content_narrative_focus,
        emotion_atmosphere: s.emotion_atmosphere,
        key_elements: s.key_elements
      }
    })
    model.matchMaterialLoading = true
    scriptShotMatch({ task_type: 0, segments }).then((data) => {
      for (let i = 0; i < data.length; i++) {
        // 清掉已选素材id，否则可能会记录上一次的选中素材
        set(model.tableData[i], 'materialCheckedId', '')
        set(model.tableData[i], 'materials', data[i])
      }
    }).finally(() => {
      model.matchMaterialLoading = false
    })
  }

  /**
   * @description: 获取单个分镜的素材
   * @param {*} s
   * @return {*}
   */
  function getShotMaterialItems(row, index) {
    if (row.shotRematchLoading) return
    const segment = {
      visual_core: row.visual_core,
      content_narrative_focus: row.content_narrative_focus,
      emotion_atmosphere: row.emotion_atmosphere,
      key_elements: row.key_elements
    }
    row.shotRematchLoading = true
    scriptShotMatch({ task_type: 1, min_similarity_threshold: row.matchThreshold / 100, segment })
      .then((data) => {
        set(model.tableData[index], 'materials', data)
      })
      .finally(() => {
        row.shotRematchLoading = false
      })
  }

  /**
 * @description: 编辑分镜
 * @param {*} index
 * @param {*} prop
 * @param {*} event
 * @return {*}
 */
  function onEditTextClick(index, prop, event) {
    const cellNode = event.target.parentNode.parentNode
    const editStatus = getRowEditStatus()
    for (const key in editStatus) {
      set(model.tableData[index], key, false)
    }
    set(model.tableData[index], prop, true)

    // 等待DOM渲染完成，自动聚焦输入框
    nextTick(() => {
      cellNode.querySelector('textarea')?.focus()
    })
  }

  /**
   * @description: 素材池显示更多
   * @param {*} row
   * @return {*}
   */
  function showMoreMaterials(row, index) {
    model.shotMaterialList = clone(row.materials)
    model.currentMaterialRowIndex = index
    model.currEditRow = row
    model.shotMaterialModalVisible = true
  }

  function onShotMaterialChange(val) {
    const rowIndex = model.currentMaterialRowIndex
    const materials = model.tableData[rowIndex].materials
    materials.forEach(m => {
      m.selected = m.id === val
    })
    model.tableData.splice(rowIndex, 1, {
      ...model.tableData[rowIndex],
      materialCheckedId: val,
      materials
    })
    model.shotMaterialModalVisible = false
  }

  return {
    ...toRefs(model),
    getScriptDetail,
    getSettingList,
    matchByScript,
    makeTableByParams,
    getScriptShotMaterialItems,
    getShotMaterialItems,
    onEditTextClick,
    showMoreMaterials,
    onShotMaterialChange
  }
}

/**
 * @description: 脚本优化
 * @return {*}
 */
export const useEditOptimize = (tableData, scriptInfo) => {
  const optimizeModel = reactive({
    scriptOptimizeModalVisible: false,
    globalOptimizeLoading: false,
    optimizationText: '',
    scriptOptimizeStep: 1,
    optimizeFlag: OPTIMIZE_FLAG.GLOBAL, // 1全局，2分镜
    shotOptimizeRowIndex: -1,
    lagecyGlobalOptimizedScript: null, // 旧版脚本
    newGlobalOptimizedScript: null // 新版优化后脚本
  })

  /**
 * @description: 打开全局脚本优化弹窗
 *  * @return {*}
 */
  const onGlobalOptimize = () => {
    if (!tableData.value?.length) return Message.error('请先添加分镜')
    for (const i in tableData.value) {
      const row = tableData.value[i]
      const { key_elements, shot_title, visual_core, emotion_atmosphere, content_narrative_focus } = row
      const keywordList = getKeywordList(key_elements)
      const index = +i + 1
      if (!shot_title.trim()) return Message.error(`分镜${index}请填写分镜标题`)
      if (!visual_core.trim()) return Message.error(`分镜${index}请填写画面描述`)
      if (!content_narrative_focus.trim()) return Message.error(`分镜${index}请填写叙事焦点`)
      if (!emotion_atmosphere.trim()) return Message.error(`分镜${index}请填写情感氛围`)
      if (!keywordList?.length) return Message.error(`分镜${index}请填写关键要素`)
    }

    optimizeModel.scriptOptimizeModalVisible = true
    optimizeModel.optimizeFlag = OPTIMIZE_FLAG.GLOBAL
  }

  /**
 * @description: 全局脚本优化
 * @return {*}
 */
  function startGlobalOptimize() {
    if (optimizeModel.optimizationText.length < 5) {
      Message.error('请填写优化内容，至少5个字符')
      return
    }
    const duration = getDuration(tableData.value)

    const shots = tableData.value.map((item) => {
      const key_elements = getKeywordList(item.key_elements)
      return {
        time_range: item.time_range,
        shot_title: item.shot_title,
        visual_core: item.visual_core,
        dialogue: item.dialogue,
        content_narrative_focus: item.content_narrative_focus,
        emotion_atmosphere: item.emotion_atmosphere,
        key_elements
      }
    })

    optimizeModel.globalOptimizeLoading = true
    scriptOptimizeCreate({
      function_type: 1,
      ai_params: {
        complete_script: {
          title: scriptInfo.value.scriptName,
          duration,
          shot_count: shots.length,
          shots
        },
        optimization_instruction: optimizeModel.optimizationText,
        target_duration: 22,
        shot_segment: 0
      }
    })
      .then((data) => {
        if (data) {
          const [script] = data
          optimizeModel.newGlobalOptimizedScript = script.shots?.map(s => {
            return {
              ...s,
              key_elements: s.key_elements.join(',')
            }
          }) || []
          optimizeModel.lagecyGlobalOptimizedScript = clone(tableData.value)
          optimizeModel.scriptOptimizeStep = 2
        }
      })
      .finally(() => {
        optimizeModel.globalOptimizeLoading = false
      })
  }

  function onGlobalOptimizeApply(getShotMaterialItems, matchByScript) {
    if (optimizeModel.optimizeFlag === OPTIMIZE_FLAG.GLOBAL) {
      tableData.value = optimizeModel.lagecyGlobalOptimizedScript.map((row, index) => {
        const compareIndex = +row.compareShotChecked
        if (compareIndex === 1) return row
        const newData = optimizeModel.newGlobalOptimizedScript[index]
        return {
          ...row,
          ...newData,
          compareShotChecked: '1' // 重置radio初始值
        }
      })

      nextTick(() => {
        typeof matchByScript === 'function' && matchByScript()
      })
    }

    if (optimizeModel.optimizeFlag === OPTIMIZE_FLAG.SHOT) {
      const compareIndex = +optimizeModel.lagecyGlobalOptimizedScript[0].compareShotChecked

      // 如果compareIndex >1 ，应用所选的是优化后的版本
      if (compareIndex > 1) {
        tableData.value.splice(optimizeModel.shotOptimizeRowIndex, 1, {
          ...tableData.value[optimizeModel.shotOptimizeRowIndex],
          ...optimizeModel.newGlobalOptimizedScript[0],
          compareShotChecked: '1'
        })

        // 选取优化后的脚本后再进行重新分镜匹配
        nextTick(() => {
          typeof getShotMaterialItems === 'function' && getShotMaterialItems(tableData.value[optimizeModel.shotOptimizeRowIndex], optimizeModel.shotOptimizeRowIndex)
        })
      }
    }

    optimizeModel.scriptOptimizeModalVisible = false
    goBack()
  }

  function goBack() {
    optimizeModel.scriptOptimizeStep = 1
    optimizeModel.newGlobalOptimizedScript = []
    optimizeModel.lagecyGlobalOptimizedScript = []
  }

  function onGlobalOptimizeCancel() {
    goBack()
  }

  /**
   * @description: 显示单条分镜优化选择弹窗
   * @param {*} shots
   * @param {*} index
   * @return {*}
   */
  function showShotOptDiffModal(shots, index) {
    optimizeModel.scriptOptimizeStep = 2
    optimizeModel.newGlobalOptimizedScript = shots.map(s => {
      return {
        ...s,
        key_elements: s.key_elements.join(',')
      }
    }) || []
    optimizeModel.lagecyGlobalOptimizedScript = [clone(tableData.value[index])]
    nextTick(() => {
      optimizeModel.scriptOptimizeModalVisible = true
      optimizeModel.optimizeFlag = OPTIMIZE_FLAG.SHOT
      optimizeModel.shotOptimizeRowIndex = index
    })
  }

  /**
 * @description: 单个分镜优化
 * @return {*}
 */
  function startScriptShotOptimize(row, index) {
    const duration = getDuration(tableData.value)
    row.shotOptimizeLoading = true
    scriptOptimizeCreate({
      function_type: 1,
      ai_params: {
        complete_script: {
          title: scriptInfo.value.scriptName,
          duration,
          shot_count: 1,
          shots: [
            {
              time_range: row.time_range,
              shot_title: row.shot_title,
              visual_core: row.visual_core,
              dialogue: row.dialogue,
              content_narrative_focus: row.content_narrative_focus,
              emotion_atmosphere: row.emotion_atmosphere,
              key_elements: getKeywordList(row.key_elements)
            }
          ]
        },
        shot_segment: 1
      }
    })
      .then((data) => {
        if (data) {
          const [newRow] = data
          showShotOptDiffModal(newRow.shots, index)
        }
      })
      .finally(() => {
        row.shotOptimizeLoading = false
      })
  }

  return {
    ...toRefs(optimizeModel),
    onGlobalOptimize,
    startScriptShotOptimize,
    startGlobalOptimize,
    onGlobalOptimizeApply,
    goBack,
    onGlobalOptimizeCancel
  }
}

/**
 * @description: 脚本保存和新增分镜
 * @return {*}
 */
export const useScriptAddAndSave = (tableData, scriptInfo, matchThreshold) => {
  const model = reactive({
    globalSaveLoading: false,
    savedScriptJSON: {}
  })

  const editingSaved = computed(() => {
    return JSON.stringify({
      name: scriptInfo.value.scriptName,
      content: tableData.value
    }) === JSON.stringify(model.savedScriptJSON)
  })

  /**
   * @description: 保存脚本
   * @return {*}
   */
  function onSaveScript(msgFlag = true) {
    if (tableData.value.length < 1) return Message.error('请至少添加一个分镜')
    if (!scriptInfo.value.scriptName) return Message.error('请填写脚本名称')
    const duration = getDuration(tableData.value)
    const shots = tableData.value.map((item) => {
      const key_elements = getKeywordList(item.key_elements)
      return {
        match_threshold: item.matchThreshold,
        time_range: item.time_range,
        shot_title: item.shot_title,
        visual_core: item.visual_core,
        dialogue: item.dialogue,
        content_narrative_focus: item.content_narrative_focus,
        emotion_atmosphere: item.emotion_atmosphere,
        key_elements,
        videos: item.materials?.map((v) => {
          return { ...v, selected: v.id === item.materialCheckedId }
        })
      }
    })
    model.globalSaveLoading = true
    const params = {
      scriptId: scriptInfo.value.scriptId,
      scriptName: scriptInfo.value.scriptName,
      scriptContent: {
        title: scriptInfo.value.scriptName,
        duration,
        shot_count: shots.length,
        shots
      }
    }
    return scriptSave(params)
      .then((data) => {
        if (data) {
          model.savedScriptJSON = clone({
            name: scriptInfo.value.scriptName,
            content: tableData.value
          })
          msgFlag && Message.success('保存成功')
        }
        return data
      })
      .finally(() => {
        model.globalSaveLoading = false
      })
  }

  function onShotItemRemove(index) {
    MessageBox.confirm('确认删除该分镜吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      tableData.value = tableData.value.filter((_, i) => i !== index)
    })
  }

  /**
 * @description: 新增分镜
 * @return {*}
 */
  function onAddShot() {
    const last = tableData.value[tableData.value.length - 1]
    const nextRange = getNextRange(last && last.time_range)
    // const nextRange = getNextRange(last && last.time_range, Math.ceil(Math.random() * 10))
    tableData.value.push({
      shot_title: '',
      dialogue: '',
      visual_core: '',
      content_narrative_focus: '',
      emotion_atmosphere: '',
      key_elements: '',
      time_range: nextRange,
      ...getRowEditStatus(),
      materialCheckedId: '', // 素材池勾选id
      compareShotChecked: '1', // 脚本智能优化对比默认选中
      matchThreshold: matchThreshold, // 片段匹配阈值
      uid: getUid()
    })
  }

  return {
    ...toRefs(model),
    editingSaved,
    onAddShot,
    onSaveScript,
    onShotItemRemove
  }
}
